import { useState, useEffect, useRef, useCallback } from "react";

import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    Select,
    TextField,
    MenuItem,
    List,
    Typography,
    Popper,
    Paper,
    ClickAwayListener,
    Checkbox,
    Tooltip,
    Alert,
    AlertTitle,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import LoadingButton from '@mui/lab/LoadingButton'

import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import ptBR from 'dayjs/locale/pt-br'
import { ptBR as localePTBR } from '@mui/x-date-pickers/locales'

import dayjs from 'dayjs'

import { getSession } from "../../auth"
import api from "../../api"
import Loading from '../Loading'
import ConfirmationDialogCaptcha from "../Dialogs/ConfimationDialogCaptcha"
import ConfirmationDialog from "../Dialogs/Confirmation";
import UserPermissions from "../User/Permissions";
import { NumericFormatInput } from "../../utils/masks";
import useNotification from '../Notification'

import OpportunityMarkers from "./opportunity-markers";
import NewCustomFieldForm from "./NewCustomFieldForm";
import { CommentBoxTextField } from "./commentbox";
import { getStandardFieldLabel, isStandardFieldHidden } from "./standard-fields";
import "./styles.css";

dayjs.locale("pt-br")

export const OpportunityByContact = ({ open, chat, contact, onClose, enterpriseId, usersEmails }) => {
    const permissions = UserPermissions()

    const fieldLabel = (key) => getStandardFieldLabel(selectedJourney, key)
    const fieldHidden = (key) => isStandardFieldHidden(selectedJourney, key)

    const [journeys, setJourneys] = useState([])
    const [selectedJourney, setSelectedJourney] = useState(null)
    const [journeyStages, setJourneyStages] = useState([])
    const [journeyUsers, setJourneyUsers] = useState([])
    const [create, setCreate] = useState(true)
    const [opportunities, setOppotunities] = useState([])
    const [selectedOpportunity, setSelectedOpportunity] = useState(null)
    const [allTags, setAllTags] = useState([])
    const [allCustomFields, setAllCustomFields] = useState([])
    const [opportunityData, setOpportunityData] = useState({})
    const [tagsOpen, setTagsOpen] = useState(false)
    const [customFieldsOpen, setCustomFieldsOpen] = useState(false)
    const [showNewTagForm, setShowNewTagForm] = useState(false)
    const [showNewCustomFieldForm, setShowNewCustomFieldForm] = useState(false)
    const [editingCustomField, setEditingCustomField] = useState(null)
    const [newTag, setNewTag] = useState({ name: '', color: '' })
    const [selectOpen, setSelectOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [showDeleteCustomFieldConfirmation, setShowDeleteCustomFieldConfirmation] = useState(null)

    const newTagsRef = useRef(null)
    const newCustomFieldsRef = useRef(null)

    useEffect(() => {
        if (open && enterpriseId) {
            fetchJourneys()
        }
    }, [open, enterpriseId])

    useEffect(() => {
        const fetchJourneyData = async () => {
            if (!selectedJourney) return

            try {
                const promises = [
                    api.get(`/api/enterprises/${enterpriseId}/journeys/${selectedJourney}/tags/`),
                    api.get(`/api/enterprises/${enterpriseId}/journeys/${selectedJourney}/custom_fields/`),
                ]

                if (!usersEmails)
                    promises.push(api.get(`/api/enterprises/${enterpriseId}/journeys/${selectedJourney}/?user_models=True`))
                else
                    promises.push(api.get(`/api/enterprises/${enterpriseId}/journeys/${selectedJourney}/`))

                const [tagsResponse, customFieldsResponse, journeyResponse] = await Promise.all(promises)

                if (tagsResponse.status === 200) {
                    setAllTags(tagsResponse.data)
                }
                if (customFieldsResponse.status === 200) {
                    setAllCustomFields(customFieldsResponse.data)
                }
                if (journeyResponse.status === 200) {
                    const stages = journeyResponse.data.columns || []
                    const sortedStages = stages.sort((a, b) => {
                        if (a.position !== undefined && b.position !== undefined) {
                            return a.position - b.position
                        }
                        if (a.order !== undefined && b.order !== undefined) {
                            return a.order - b.order
                        }
                        return 0
                    })
                    setJourneyStages(sortedStages)
                    if (!usersEmails)
                        setJourneyUsers(journeyResponse.data.users || [])
                    else {
                        const journeyUserIds = journeyResponse.data.users || []
                        const filteredUsers = usersEmails.filter(user => journeyUserIds.includes(user.id))
                        setJourneyUsers(filteredUsers)
                    }
                }
            } catch (error) {
                console.error('Erro ao buscar dados da jornada:', error)
            }
        }
        fetchJourneyData()
    }, [selectedJourney, enterpriseId, usersEmails])

    useEffect(() => {
        if (open && contact) {
            const myuser = getSession()
            setOpportunityData({
                title: '',
                description: '',
                value: '',
                currency: 'BRL',
                status: 'ACTIVE',
                column: null,
                owner: permissions.isSupervisor ? '' : myuser.id,
                estimated_at: null,
                closed_at: null,
                tags: [],
                custom_fields: {},
                secondary_contacts: '',
                contact_enterprise: contact.contact_enterprise || null
            })
        }
    }, [open, contact, permissions.isSupervisor])

    const fetchJourneys = async () => {
        try {
            setLoading(true)
            const response = await api.get(`/api/enterprises/${enterpriseId}/journeys/`)
            if (response.status === 200) {
                const journeysData = response.data.results || response.data
                setJourneys(journeysData)
            }
        } catch (error) {
            console.error('Erro ao buscar jornadas:', error)
            setJourneys([])
        } finally {
            setLoading(false)
        }
    }

    const handleCloseModal = () => {
        setSelectedJourney(null)
        setJourneyStages([])
        setJourneys([])
        setAllTags([])
        setAllCustomFields([])
        setJourneyUsers([])
        setOpportunityData({})
        setTagsOpen(false)
        setCustomFieldsOpen(false)
        setShowNewTagForm(false)
        setShowNewCustomFieldForm(false)
        setEditingCustomField(null)
        setNewTag({ name: '', color: '' })
        onClose()
    }

    const handleSaveCustomField = async (fieldData) => {
        try {
            const response = await api.post(`/api/enterprises/${enterpriseId}/journeys/${selectedJourney}/custom_fields/`, fieldData)
            if (response.status === 201) {
                setAllCustomFields((prevState) => ([...prevState, response.data]))
                setShowNewCustomFieldForm(false)
                useNotification("Sucesso", "Campo personalizado criado com sucesso", "success")
            }
        } catch (error) {
            console.error('Erro ao criar campo personalizado:', error)
            useNotification("Erro", "Erro ao criar campo personalizado", "danger")
        }
    }

    const handleEditCustomField = async (fieldData) => {
        try {
            const response = await api.patch(`/api/enterprises/${enterpriseId}/journeys/${selectedJourney}/custom_fields/${editingCustomField.id}/`, fieldData)
            if (response.status === 200) {
                setAllCustomFields((prevState) => prevState.map(field => field.id === editingCustomField.id ? response.data : field))
                setEditingCustomField(null)
                setShowNewCustomFieldForm(false)
                useNotification("Sucesso", "Campo personalizado atualizado com sucesso", "success")
            }
        } catch (error) {
            console.error('Erro ao editar campo personalizado:', error)
            useNotification("Erro", "Erro ao editar campo personalizado", "danger")
        }
    }

    const deleteCustomField = async (fieldId) => {
        try {
            const response = await api.delete(`api/enterprises/${enterpriseId}/journeys/${selectedJourney}/custom_fields/${fieldId}/`)

            if (response.status === 204) {
                setAllCustomFields((prevState) => prevState.filter(field => field.id !== fieldId))

                setOpportunityData((prevState) => {
                    const updatedCustomFields = { ...prevState.custom_fields }
                    delete updatedCustomFields[fieldId]
                    return {
                        ...prevState,
                        custom_fields: updatedCustomFields
                    }
                })
                useNotification("Sucesso", "Campo personalizado excluído com sucesso", "success")
            }
        } catch (error) {
            console.error('Erro ao excluir campo personalizado:', error)
            useNotification("Erro", "Erro ao excluir campo personalizado", "danger")
        }
    }

    const updateCustomFieldValue = (fieldId, value) => {
        setOpportunityData((prevState) => ({
            ...prevState,
            custom_fields: {
                ...prevState.custom_fields,
                [fieldId]: value
            }
        }))
    }

    const renderCustomFieldInput = (field) => {
        const value = opportunityData.custom_fields?.[field.id] || ''

        switch (field.field_type) {
            case 'text':
                return (
                    <TextField
                        key={field.id}
                        size="small"
                        label={field.name + (field.required ? ' *' : '')}
                        placeholder={field.placeholder || ''}
                        value={value}
                        onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
                        required={field.required}
                        fullWidth
                    />
                )
            case 'textarea':
                return (
                    <TextField
                        key={field.id}
                        size="small"
                        label={field.name + (field.required ? ' *' : '')}
                        placeholder={field.placeholder || ''}
                        value={value}
                        onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
                        multiline
                        rows={3}
                        required={field.required}
                        fullWidth
                    />
                )
            case 'number':
                return (
                    <TextField
                        key={field.id}
                        size="small"
                        label={field.name + (field.required ? ' *' : '')}
                        placeholder={field.placeholder || ''}
                        type="number"
                        value={value}
                        onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
                        required={field.required}
                        fullWidth
                    />
                )
            case 'select':
                return (
                    <FormControl key={field.id} size="small" fullWidth required={field.required}>
                        <InputLabel>{field.name + (field.required ? ' *' : '')}</InputLabel>
                        <Select
                            value={value}
                            onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
                            label={field.name + (field.required ? ' *' : '')}
                        >
                            {field.options?.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )
            case 'date':
                return (
                    <DemoContainer
                        key={field.id}
                        components={['DatePicker']}
                        sx={{ paddingTop: 0, minWidth: 0, overflow: 'visible' }}
                    >
                        <DateTimePicker
                            label={field.name + (field.required ? ' *' : '')}
                            value={value ? dayjs(value) : null}
                            onChange={(newValue) => updateCustomFieldValue(field.id, newValue ? newValue.format('YYYY-MM-DD') : null)}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    required: field.required,
                                    fullWidth: true
                                }
                            }}
                        />
                    </DemoContainer>
                )
            case 'checkbox':
                return (
                    <Box key={field.id} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={value || false}
                            onChange={(e) => updateCustomFieldValue(field.id, e.target.checked)}
                        />
                        <Typography>{field.name + (field.required ? ' *' : '')}</Typography>
                    </Box>
                )
            default:
                return null
        }
    }

    const handleSave = async () => {
        if (!selectedJourney || !opportunityData.column) {
            useNotification("Atenção", "Selecione uma jornada e um estágio", "warning")
            return
        }

        setLoading(true)
        try {

            if (!create) {
                const payload = {
                    opportunity: selectedOpportunity,
                    chat: chat?.uniqueid,
                }
                const response = await api.post(
                    `/api/enterprises/${enterpriseId}/journeys/${selectedJourney}/opportunities/chat/`,
                    payload
                )
                if (response.status === 200) {
                    useNotification("Sucesso", "Chat vinculado com sucesso", "success")
                    handleCloseModal()
                }
                else if (response.status === 206) {
                    useNotification("Sucesso", "Chat já está vinculado com essa oportunidade.", "success")
                    handleCloseModal()
                }
                else
                    useNotification("Erro", "Erro ao vincular chat", "danger")

                return
            }

            const dataToSend = {
                column: String(opportunityData.column),
                status: opportunityData.status,
                title: opportunityData.title,
                description: opportunityData.description,
                value: opportunityData.value ? parseFloat(opportunityData.value) : 0,
                currency: opportunityData.currency,
                owner: opportunityData.owner || null,
                contact_id: contact?.id,
                contact_enterprise_id: opportunityData.contact_enterprise || contact?.contact_enterprise || null,
                secondary_contacts: opportunityData.secondary_contacts,
                estimated_at: opportunityData.estimated_at ? opportunityData.estimated_at : null,
                closed_at: opportunityData.closed_at ? opportunityData.closed_at : null,
                history: [],
                comments: [],
                tags: opportunityData.tags || [],
                custom_fields: opportunityData.custom_fields,
                chat: chat?.uniqueid,
                id: 0
            }

            const response = await api.post(
                `/api/enterprises/${enterpriseId}/journeys/${selectedJourney}/opportunities/`,
                dataToSend
            )

            if (response.status === 201) {
                if (opportunityData.custom_fields && Object.keys(opportunityData.custom_fields).length > 0) {
                    const customFieldsPayload = Object.entries(opportunityData.custom_fields)
                        .filter(([key, value]) => value !== null && value !== undefined && value !== '')
                        .map(([key, value]) => ({
                            custom_field: parseInt(key),
                            value: value
                        }))

                    if (customFieldsPayload.length > 0) {
                        await api.post(
                            `/api/enterprises/${enterpriseId}/journeys/${selectedJourney}/opportunities/${response.data.id}/custom_fields/?created=true`,
                            customFieldsPayload
                        )
                    }
                }

                useNotification("Sucesso", "Oportunidade criada com sucesso", "success")
                handleCloseModal()
            }
        } catch (error) {
            console.error('Erro ao criar oportunidade:', error)
            useNotification("Erro", "Erro ao criar oportunidade", "danger")
        } finally {
            setLoading(false)
        }
    }

    const requestOpportunities = useCallback(async (column) => {
        setLoading(true)
        const response = await api.get(`/api/enterprises/${enterpriseId}/journeys/${selectedJourney}/columns/${column}/`)
        setLoading(false)
        if (response.status === 200) {
            setOppotunities(response.data.cards)
        }
    }, [selectedJourney])


    if (!open) return null

    return (
        <>
            <Dialog
                open={open}
                onClose={() => { }}
                maxWidth="md"
                fullWidth
                className="opportunity-by-contact-modal"
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Nova Oportunidade - {contact?.name}</span>
                    <Tooltip title="Fechar">
                        <Button className="action-button">
                            <CloseIcon onClick={handleCloseModal} />
                        </Button>
                    </Tooltip>
                </DialogTitle>
                <Divider />
                <DialogContent className="opportunity-by-contact-modal">
                    {(loading || journeys.length !== 0) &&
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={ptBR} localeText={localePTBR.components.MuiLocalizationProvider.defaultProps.localeText}>
                            <Box className="opportunity-data" sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '1rem' }}>
                                <FormControl size="small" fullWidth required>
                                    <InputLabel>Jornada</InputLabel>
                                    <Select
                                        value={selectedJourney || ''}
                                        onChange={(e) => {
                                            setSelectedJourney(e.target.value)
                                            setEditingCustomField(null)
                                            setShowNewCustomFieldForm(false)
                                            setOpportunityData(prev => ({ ...prev, column: null }))
                                        }}
                                        label="Jornada"
                                    >
                                        {journeys.map((journey) => (
                                            <MenuItem key={journey.id} value={journey.id}>
                                                {journey.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" fullWidth required disabled={!selectedJourney}>
                                    <InputLabel>Estágio</InputLabel>
                                    <Select
                                        value={opportunityData.column || ''}
                                        onChange={(e) => {
                                            setOpportunityData(prev => ({ ...prev, column: e.target.value }))
                                            setOppotunities([])
                                            setSelectedOpportunity(null)
                                            requestOpportunities(e.target.value)
                                        }}
                                        label="Estágio"
                                    >
                                        {!selectedJourney && <MenuItem value="">Selecione uma jornada primeiro</MenuItem>}
                                        {journeyStages.length === 0 && selectedJourney && <MenuItem value="">Carregando estágios...</MenuItem>}
                                        {journeyStages.map((stage) => (
                                            <MenuItem key={stage.id} value={stage.id}>
                                                {stage.title}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {chat &&
                                    <Box className='BuilderBotSelectorBox' >
                                        <Button variant={create ? 'contained' : 'standard'} onClick={() => setCreate(true)}>Criar</Button>
                                        <Button variant={!create ? 'contained' : 'standard'} onClick={() => setCreate(false)}>Vincular</Button>
                                    </Box>}
                                {create ? <>
                                    {!fieldHidden("status") && (
                                    <FormControl size="small" fullWidth required>
                                        <InputLabel>{fieldLabel("status")}</InputLabel>
                                        <Select
                                            value={opportunityData.status || 'ACTIVE'}
                                            onChange={(e) => setOpportunityData(prev => ({ ...prev, status: e.target.value }))}
                                            label={fieldLabel("status")}
                                        >
                                            <MenuItem value="ACTIVE">Ativa</MenuItem>
                                            <MenuItem value="QUALIFIED">Qualificada</MenuItem>
                                            <MenuItem value="CONQUERED">Conquistada</MenuItem>
                                            <MenuItem value="INACTIVE">Inativa</MenuItem>
                                            <MenuItem value="LOST">Perdida</MenuItem>
                                        </Select>
                                    </FormControl>
                                    )}

                                    <TextField
                                        size="small"
                                        label={fieldLabel("title")}
                                        placeholder="Nova oportunidade"
                                        value={opportunityData.title || ''}
                                        onChange={(e) => setOpportunityData(prev => ({ ...prev, title: e.target.value }))}
                                        required
                                        inputProps={{ maxLength: 255 }}
                                        fullWidth
                                    />

                                    {!fieldHidden("description") && (
                                        <Box className="opportunity-by-contact-description-editor">
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {fieldLabel("description")}
                                            </Typography>

                                            <CommentBoxTextField
                                                value={opportunityData.description || ''}
                                                onChange={({ target }) =>
                                                    setOpportunityData(prev => ({
                                                        ...prev,
                                                        description: target.value,
                                                    }))
                                                }
                                                id="opportunity-by-contact-description"
                                            />
                                        </Box>
                                    )}

                                    {!fieldHidden("value") && (
                                    <Box className="currency-row" sx={{ display: 'flex', gap: '1rem' }}>
                                        <TextField
                                            size="small"
                                            label={fieldLabel("value")}
                                            value={opportunityData.value || ''}
                                            onChange={(e) => setOpportunityData(prev => ({ ...prev, value: e.target.value }))}
                                            sx={{ flex: 2 }}
                                            inputProps={{ max: 9999999999 }}
                                            InputProps={{
                                                inputComponent: NumericFormatInput,
                                            }}
                                            required={selectedJourney?.required_opportunity_fields?.includes('value')}
                                        />
                                        <FormControl size="small" sx={{ flex: 1 }}>
                                            <InputLabel>Moeda</InputLabel>
                                            <Select
                                                value={opportunityData.currency || 'BRL'}
                                                onChange={(e) => setOpportunityData(prev => ({ ...prev, currency: e.target.value }))}
                                                label="Moeda"
                                            >
                                                <MenuItem value="BRL">BRL</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    )}

                                    {!fieldHidden("owner") && (
                                    <FormControl size="small" fullWidth disabled={!selectedJourney || !permissions.isSupervisor} required={selectedJourney?.required_opportunity_fields?.includes('owner')}>
                                        <InputLabel>{fieldLabel("owner")}</InputLabel>
                                        <Select
                                            value={opportunityData.owner || ''}
                                            onChange={(e) => setOpportunityData(prev => ({ ...prev, owner: e.target.value }))}
                                            label={fieldLabel("owner")}
                                        >
                                            {!selectedJourney && <MenuItem value="">Selecione uma jornada primeiro</MenuItem>}
                                            {journeyUsers.length === 0 && selectedJourney && <MenuItem value="">Carregando vendedores...</MenuItem>}
                                            {journeyUsers.map((user) => (
                                                <MenuItem key={user.id} value={user.id}>
                                                    {`${user.first_name || ''} ${user.last_name || ''}`.trim()}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    )}

                                    {!fieldHidden("contact_enterprise") && (
                                    <TextField
                                        size="small"
                                        label={fieldLabel("contact_enterprise")}
                                        value={contact?.contact_enterprise_name || ""}
                                        fullWidth
                                        disabled
                                    />
                                    )}
 
                                    {!fieldHidden("contact") && (
                                    <TextField
                                        size="small"
                                        label={fieldLabel("contact")}
                                        value={contact?.name || ""}
                                        fullWidth
                                        disabled
                                        required={selectedJourney?.required_opportunity_fields?.includes('contact')}
                                    />
                                    )}
 
                                    {!fieldHidden("secondary_contacts") && (
                                    <TextField
                                        size="small"
                                        label={fieldLabel("secondary_contacts")}
                                        value={opportunityData.secondary_contacts || ''}
                                        onChange={(e) => setOpportunityData(prev => ({ ...prev, secondary_contacts: e.target.value }))}
                                        fullWidth
                                    />
                                    )}
 
                                    {(!fieldHidden("estimated_at") || !fieldHidden("closed_at")) && (
                                    <Box sx={{ display: 'flex', gap: '1rem' }}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                                            {!fieldHidden("estimated_at") && (
                                            <DemoContainer
                                                components={['DateTimePicker']}
                                                sx={{ flex: 1, paddingTop: 0, minWidth: 0, overflow: 'visible' }}
                                            >
                                                <DateTimePicker
                                                    label={fieldLabel("estimated_at")}
                                                    value={opportunityData.estimated_at ? dayjs(opportunityData.estimated_at) : null}
                                                    onChange={(newValue) => setOpportunityData(prev => ({ ...prev, estimated_at: newValue }))}
                                                    format="DD/MM/YYYY HH:mm"
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            fullWidth: true,
                                                            required: selectedJourney?.required_opportunity_fields?.includes('estimated_at')
                                                        }
                                                    }}
                                                />
                                            </DemoContainer>
                                            )}
                                            {!fieldHidden("closed_at") && (
                                            <DemoContainer
                                                components={['DateTimePicker']}
                                                sx={{ flex: 1, paddingTop: 0, minWidth: 0, overflow: 'visible' }}
                                            >
                                                <DateTimePicker
                                                    label={fieldLabel("closed_at")}
                                                    value={opportunityData.closed_at ? dayjs(opportunityData.closed_at) : null}
                                                    onChange={(newValue) => setOpportunityData(prev => ({ ...prev, closed_at: newValue }))}
                                                    format="DD/MM/YYYY HH:mm"
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            fullWidth: true
                                                        }
                                                    }}
                                                />
                                            </DemoContainer>
                                            )}
                                        </LocalizationProvider>
                                    </Box>
                                    )}

                                    <Box className="tags-container">
                                        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                                            <Typography variant="h6" sx={{ marginRight: 'auto' }}>
                                                Marcadores
                                            </Typography>
                                            <IconButton
                                                ref={newTagsRef}
                                                onClick={() => {
                                                    if (selectedJourney) {
                                                        setTagsOpen((prev) => !prev)
                                                    }
                                                }}
                                                size="small"
                                                disabled={!selectedJourney}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        </Box>

                                        {!selectedJourney ? (
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                Selecione uma jornada para ver os marcadores
                                            </Typography>
                                        ) : allTags.length === 0 ? (
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                Esta jornada não possui marcadores
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                {allTags.map((tag) => (
                                                    <Box
                                                        key={tag.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '0.25rem 0.5rem',
                                                            borderRadius: '4px',
                                                            border: `2px solid ${tag.color}`,
                                                            cursor: 'pointer',
                                                            backgroundColor: opportunityData.tags?.includes(tag.id) ? tag.color + '40' : 'transparent'
                                                        }}
                                                        onClick={() => {
                                                            const currentTags = opportunityData.tags || []
                                                            if (currentTags.includes(tag.id)) {
                                                                setOpportunityData(prev => ({
                                                                    ...prev,
                                                                    tags: currentTags.filter(id => id !== tag.id)
                                                                }))
                                                            } else {
                                                                setOpportunityData(prev => ({
                                                                    ...prev,
                                                                    tags: [...currentTags, tag.id]
                                                                }))
                                                            }
                                                        }}
                                                    >
                                                        <Typography variant="body2">{tag.name}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        {selectedJourney && (
                                            <OpportunityMarkers
                                                tagsOpen={tagsOpen}
                                                setTagsOpen={setTagsOpen}
                                                allTags={allTags}
                                                newTagsRef={newTagsRef}
                                                setAllTags={setAllTags}
                                                setParent={setOpportunityData}
                                                parent={opportunityData}
                                                selectedJourney={{ id: selectedJourney }}
                                            />
                                        )}
                                    </Box>

                                    {/* Campos Personalizados */}
                                    <Box className="custom-fields-container">
                                        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                                            <Typography variant="h6" sx={{ marginRight: 'auto' }}>
                                                Campos Personalizados
                                            </Typography>
                                            <IconButton
                                                ref={newCustomFieldsRef}
                                                onClick={() => {
                                                    if (selectedJourney) {
                                                        setCustomFieldsOpen((prev) => !prev)
                                                    }
                                                }}
                                                size="small"
                                                disabled={!selectedJourney}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        </Box>

                                        {!selectedJourney ? (
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                Selecione uma jornada para ver os campos personalizados
                                            </Typography>
                                        ) : allCustomFields.length === 0 ? (
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                Esta jornada não possui campos personalizados
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                {allCustomFields.map((field) => renderCustomFieldInput(field))}
                                            </Box>
                                        )}

                                        {selectedJourney && (
                                            <Popper
                                                open={customFieldsOpen}
                                                anchorEl={newCustomFieldsRef.current}
                                                placement="left-start"
                                                sx={{ zIndex: 1300 }}
                                            >
                                                <ClickAwayListener onClickAway={() => {
                                                    if (selectOpen) return
                                                    setCustomFieldsOpen(false)
                                                    setShowNewCustomFieldForm(false)
                                                }}>
                                                    <Paper>
                                                        {!showNewCustomFieldForm && (
                                                            <Box sx={{ padding: '1rem', minWidth: '300px' }}>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                                                    <Typography variant="h6">Campos Personalizados</Typography>
                                                                    <IconButton size="small" onClick={() => setCustomFieldsOpen(false)}>
                                                                        <CloseIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                                {allCustomFields.length > 0 && (
                                                                    <Box sx={{ marginBottom: '1rem' }}>
                                                                        <Typography variant="body2" sx={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                                                            Campos existentes:
                                                                        </Typography>
                                                                        {allCustomFields.map((field) => (
                                                                            <Box key={field.id} sx={{ padding: '0.25rem 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                                    • {field.name} (
                                                                                    {field.field_type === 'text'
                                                                                        ? 'Texto'
                                                                                        : field.field_type === 'textarea'
                                                                                            ? 'Texto longo'
                                                                                            : field.field_type === 'number'
                                                                                                ? 'Número'
                                                                                                : field.field_type === 'select'
                                                                                                    ? 'Lista'
                                                                                                    : field.field_type === 'date'
                                                                                                        ? 'Data'
                                                                                                        : field.field_type === 'checkbox'
                                                                                                            ? 'Checkbox'
                                                                                                            : field.field_type}
                                                                                    ){field.required && ' - Obrigatório'}{field.show_in_summary && ' - Resumo'}
                                                                                </Typography>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="primary"
                                                                                    onClick={() => {
                                                                                        setEditingCustomField(field)
                                                                                        setShowNewCustomFieldForm(true)
                                                                                    }}
                                                                                    sx={{ ml: 1 }}
                                                                                >
                                                                                    <EditIcon fontSize="small" />
                                                                                </IconButton>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="error"
                                                                                    onClick={() => {
                                                                                        setShowDeleteCustomFieldConfirmation(field)
                                                                                    }}
                                                                                    sx={{ ml: 1 }}
                                                                                >
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                                <Button
                                                                    className="opportunity-btn"
                                                                    onClick={() => {
                                                                        setEditingCustomField(null)
                                                                        setShowNewCustomFieldForm(true)
                                                                    }}
                                                                    fullWidth
                                                                >
                                                                    <AddIcon />
                                                                    Criar campo personalizado
                                                                </Button>
                                                            </Box>
                                                        )}
                                                        {showNewCustomFieldForm && (
                                                            <NewCustomFieldForm
                                                                initialField={editingCustomField}
                                                                onBack={() => {
                                                                    setEditingCustomField(null)
                                                                    setShowNewCustomFieldForm(false)
                                                                }}
                                                                onSave={editingCustomField ? handleEditCustomField : handleSaveCustomField}
                                                                onSelectOpenChange={(isOpen) => setSelectOpen(isOpen)}
                                                            />
                                                        )}
                                                        {showDeleteCustomFieldConfirmation && (
                                                            <ConfirmationDialogCaptcha
                                                                open={showDeleteCustomFieldConfirmation}
                                                                title="Confirmação"
                                                                description={
                                                                    <Box>
                                                                        Para remover o campo personalizado "
                                                                        {showDeleteCustomFieldConfirmation.name}", digite:{" "}
                                                                        <span style={{ fontWeight: "bold" }}>remover</span> no campo
                                                                        abaixo.
                                                                    </Box>
                                                                }
                                                                handleClose={() => setShowDeleteCustomFieldConfirmation(null)}
                                                                handleSubmit={() => {
                                                                    deleteCustomField(showDeleteCustomFieldConfirmation.id);
                                                                    setShowDeleteCustomFieldConfirmation(null);
                                                                }}
                                                                captcha={"remover"}
                                                            />
                                                        )}
                                                    </Paper>
                                                </ClickAwayListener>
                                            </Popper>
                                        )}
                                    </Box>
                                </> :
                                    <FormControl size="small" fullWidth required disabled={!opportunityData.column}>
                                        <InputLabel>Oportunidade</InputLabel>
                                        <Select
                                            value={selectedOpportunity}
                                            onChange={(e) => setSelectedOpportunity(e.target.value)}
                                            label="Oportunidade"
                                        >
                                            {!opportunityData.column && <MenuItem value="">Selecione um estágio</MenuItem>}
                                            {opportunities.length === 0 && loading && <MenuItem value="">Carregando oportunidades...</MenuItem>}
                                            {opportunities.map((opportunity) => (
                                                <MenuItem key={opportunity.id} value={opportunity.id}>
                                                    {opportunity.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                }
                            </Box>
                        </LocalizationProvider>
                    }
                    {!loading && journeys.length === 0 &&
                        <Alert severity="info">
                            <AlertTitle>Sem jornadas!</AlertTitle>
                            Você não faz parte de nenhum jornada para poder criar uma nova oportunidade.
                        </Alert>
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal}>Cancelar</Button>
                    <LoadingButton
                        onClick={handleSave}
                        loading={loading}
                        disabled={!selectedJourney || !opportunityData.column || (!create && !selectedOpportunity)}
                        startIcon={<SaveIcon />}
                        variant="contained"
                    >
                        Salvar
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}
